/**
 * Admin Users Screen
 * 
 * Comprehensive user management with:
 * - User listing with search/filter
 * - Role assignment modal with scope selection
 * - Group membership management
 * - Permission status display
 */

import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Shield, 
  CheckCircle, 
  XCircle, 
  RefreshCw, 
  Users, 
  X,
  Search,
  UserPlus,
  Edit,
  Trash2,
  Pencil,
  UserCheck,
  Eye,
  EyeOff,
  AlertCircle,
  Check,
  KeyRound,
  Building2,
  Copy,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { getSupabaseClient } from '../../../lib/supabaseClient';
import { resolveAdminScope } from '../../../lib/adminScope';
import { createUserWithPassword, linkExistingUserToWorkspace, isAuthEmailAlreadyRegisteredMessage } from '../../../lib/createUserService';
import { resetUserPassword } from '../../../lib/resetPasswordService';
import { isImpersonationEnabled } from '../../../lib/impersonationConfig';
import { isSuperAdminBySupabaseUserId } from '../../../lib/superAdminService';
import {
  updateRoleAssignment,
  fetchUserAssignments,
  type ClonedUserAssignments,
} from '../../../lib/rolesService';
import {
  createRoleAssignmentRpc,
  deleteRoleAssignmentRpc,
  deleteRoleAssignmentsForSubjectRpc,
} from '../../../lib/delegationRpc';
import type { ScopeKind } from '../../../types/prioritization/permissions';
import type { OrgUnit } from '../../../types/prioritization/enterpriseIdentity';
import { InlineGroupCreator } from './_InlineGroupCreator';
import { AssignRoleModal } from '../../../components/roles/AssignRoleModal';
import { SearchableSelect } from '../../../components/shared/SearchableSelect';
import {
  ROLE_PACKAGES,
  ROLE_PACKAGE_THEME_LABELS,
  getRoleSlugTier,
  getRolePackage,
  type RolePackageDefinition,
} from '../../../types/prioritization/rolePackages';
import { DEFAULT_JOB_ROLES, JOB_FAMILIES } from '../../../types/prioritization/jobRoles';

// ============================================================================
// Types
// ============================================================================

type ActivityStatus = 'active' | 'idle' | 'inactive' | 'never_logged_in';

function computeActivityStatus(lastSeenAt: string | null): ActivityStatus {
  if (!lastSeenAt) return 'never_logged_in';
  const days = (Date.now() - new Date(lastSeenAt).getTime()) / 86_400_000;
  if (days <= 30) return 'active';
  if (days <= 90) return 'idle';
  return 'inactive';
}

function formatLastLogin(lastLoginAt: string | null): string {
  if (!lastLoginAt) return 'Never';
  const date = new Date(lastLoginAt);
  const days = (Date.now() - date.getTime()) / 86_400_000;
  if (days < 1) return 'Today';
  if (days < 2) return 'Yesterday';
  if (days < 7) return `${Math.floor(days)}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: days > 365 ? 'numeric' : undefined });
}

interface UserWithPermissions {
  id: string;
  externalId: string | null;
  name: string;
  email: string;
  createdAt: string;
  lastLoginAt: string | null;
  lastSeenAt: string | null;
  activityStatus: ActivityStatus;
  roles: RoleAssignment[];
  inheritedRoles: InheritedRole[];
  groups: GroupMembership[];
  isActive: boolean;
}

interface RoleAssignment {
  id: string;
  roleId: string;
  roleLabel: string;
  tier: number;
  scopeKind: string;
  scopeId: string;
}

interface InheritedRole {
  roleId: string;
  roleLabel: string;
  tier: number;
  scopeKind: string;
  scopeId: string;
  /** The group that carries this role */
  viaGroupId: string;
  viaGroupName: string;
}

interface GroupMembership {
  id: string;
  groupId: string;
  groupName: string;
  isPrimary: boolean;
}

interface Role {
  id: string;
  label: string;
  permissionTier: number;
  description: string;
}

interface Group {
  id: string;
  name: string;
  type: string;
}

/**
 * Deferred role/group/package assignment recorded during user creation.
 * Applied to the new user inside the onSuccess handler after the account exists.
 */
export type PendingAssignment =
  | { mode: 'package'; pkg: RolePackageDefinition }
  | { mode: 'group'; groupId: string }
  | { mode: 'roles'; roleIds: string[]; scopeOrgUnitId: string }
  | null;

// ============================================================================
// Shared assignment-application helpers
//
// These are used both by the main component's onSuccess handler (applying the
// single-mode PendingAssignment picked in CreateUserModal's Package/Group/
// Individual-Roles tabs) and by CreateUserModal's clone-on-create flow
// (applying cloned items that fall outside that single-mode selection, e.g.
// extra groups or roles left over after a package match). Keeping one
// implementation avoids the two call sites drifting apart.
// ============================================================================

/** Individual role assignment input matching create_role_assignment's scope union. */
interface IndividualRoleAssignmentInput {
  roleId: string;
  scopeKind: 'global' | 'organization' | 'group' | 'resource';
  scopeId: string;
}

async function applyIndividualRoleAssignments(userId: string, items: IndividualRoleAssignmentInput[]): Promise<void> {
  for (const item of items) {
    const raResult = await createRoleAssignmentRpc({
      subjectType: 'user',
      subjectId: userId,
      roleId: item.roleId,
      scopeKind: item.scopeKind,
      scopeId: item.scopeId || '*',
      tier: 'supporting',
    });
    if (!raResult.success) console.warn('[AdminUsers] Role assignment failed:', raResult.error);
  }
}

async function applyGroupMemberships(userId: string, groupIds: string[]): Promise<void> {
  if (groupIds.length === 0) return;
  const supabase = getSupabaseClient();
  for (const groupId of groupIds) {
    const { error } = await supabase.from('memberships').insert({ user_id: userId, group_id: groupId });
    if (error) console.warn('[AdminUsers] Group assignment failed:', error.message);
  }
}

// ============================================================================
// Main Component
// ============================================================================

export default function AdminUsers() {
  const { switchRole, userId: currentUserId, startImpersonation, currentRole, assignedRoleNames } = useUserStore();
  const { orgUnits: orgUnitsMap, fetchFromSupabase: fetchOrgUnits } = useEnterpriseIdentityStore();
  const addNotification = useUIStore((state) => state.addNotification);
  
  // Get current tenant/workspace from Zustand store (the single source of truth)
  const storeTenantId = useTenantStore((state) => state.currentTenantId);
  const storeWorkspaceId = useTenantStore((state) => state.currentWorkspaceId);
  const adminScope = useMemo(
    () => resolveAdminScope(currentRole, assignedRoleNames),
    [currentRole, assignedRoleNames],
  );
  
  // Convert org units Map to array for passing to modal
  const orgUnitsList = Array.from(orgUnitsMap.values());
  
  // Data state
  const [users, setUsers] = useState<UserWithPermissions[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserWithPermissions | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  // Track which users have their full role list expanded (collapsed by default when > ROLES_COLLAPSE_AFTER)
  const [expandedRoleUsers, setExpandedRoleUsers] = useState<Set<string>>(new Set());
  const ROLES_COLLAPSE_AFTER = 4;
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingRoleAssignment, setEditingRoleAssignment] = useState<RoleAssignment | null>(null);
  const [editingUser, setEditingUser] = useState<UserWithPermissions | null>(null);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState<UserWithPermissions | null>(null);

  // ========================================================================
  // Data Fetching
  // ========================================================================

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('[AdminUsers] Starting data fetch...');
      const supabase = getSupabaseClient();
      console.log('[AdminUsers] Supabase client obtained, fetching data...');

      const { currentRole: scopedActorRole, assignedRoleNames: scopedAssignments } = useUserStore.getState();
      const adminScopeForFetch = resolveAdminScope(scopedActorRole, scopedAssignments);
      
      // Scope by admin role: workspace-admin sees only their workspace; tenant-admin/platform see tenant
      let userIdsFilter: string[] | null = null;
      if (adminScopeForFetch === 'workspace') {
        let effectiveWorkspaceId = storeWorkspaceId;
        // If switcher didn't set a workspace (e.g. workspace-admin with no tenant in switcher), use their only workspace
        if (!effectiveWorkspaceId && currentUserId) {
          const { data: myMemberships } = await supabase
            .from('workspace_memberships')
            .select('workspace_id')
            .eq('user_id', currentUserId);
          if (myMemberships?.length === 1) {
            effectiveWorkspaceId = myMemberships[0].workspace_id;
            console.log('[AdminUsers] Workspace admin: using sole workspace', effectiveWorkspaceId);
          }
        }
        if (!effectiveWorkspaceId) {
          console.warn('[AdminUsers] Workspace admin must have a workspace selected or be in exactly one workspace');
          setUsers([]);
          setRoles([]);
          setGroups([]);
          setLoading(false);
          return;
        }
        const { data: wsMemberships } = await supabase
          .from('workspace_memberships')
          .select('user_id')
          .eq('workspace_id', effectiveWorkspaceId);
        if (wsMemberships && wsMemberships.length > 0) {
          userIdsFilter = [...new Set(wsMemberships.map((m: { user_id: string }) => m.user_id))];
          // Exclude platform admins (super_admins) — they can access the workspace but are not workspace users
          const { data: superAdminRows } = await supabase.from('super_admins').select('user_id');
          const superAdminIds = new Set((superAdminRows || []).map((r: { user_id: string }) => r.user_id));
          if (superAdminIds.size > 0) {
            userIdsFilter = userIdsFilter.filter((id) => !superAdminIds.has(id));
          }
          console.log('[AdminUsers] Filtering to', userIdsFilter.length, 'users for workspace', effectiveWorkspaceId);
        } else {
          setUsers([]);
          setRoles([]);
          setGroups([]);
          setLoading(false);
          return;
        }
      } else {
        // tenant or platform: users in this tenant (tenant_memberships) OR in any workspace under this tenant (workspace_memberships)
        const currentTenantId = storeTenantId;
        if (!currentTenantId) {
          console.warn('[AdminUsers] No tenant selected - please select a tenant first');
          setUsers([]);
          setRoles([]);
          setGroups([]);
          setLoading(false);
          return;
        }
        const { data: tenantMemberships } = await supabase
          .from('tenant_memberships')
          .select('user_id')
          .eq('tenant_id', currentTenantId);
        const tenantUserIds = new Set((tenantMemberships || []).map((tm: { user_id: string }) => tm.user_id));
        // Include users who are in a workspace under this tenant but not yet in tenant_memberships (e.g. granted via Platform Admin only)
        const { data: workspacesInTenant } = await supabase
          .from('workspaces')
          .select('id')
          .eq('tenant_id', currentTenantId);
        const workspaceIds = (workspacesInTenant || []).map((w: { id: string }) => w.id);
        if (workspaceIds.length > 0) {
          const { data: wsMemberships } = await supabase
            .from('workspace_memberships')
            .select('user_id')
            .in('workspace_id', workspaceIds);
          (wsMemberships || []).forEach((m: { user_id: string }) => tenantUserIds.add(m.user_id));
        }
        if (tenantUserIds.size > 0) {
          userIdsFilter = [...tenantUserIds];
          // Exclude platform admins (super_admins) so tenant admin never sees them in the list
          const { data: superAdminRows } = await supabase.from('super_admins').select('user_id');
          const superAdminIds = new Set((superAdminRows || []).map((r: { user_id: string }) => r.user_id));
          if (superAdminIds.size > 0) {
            userIdsFilter = userIdsFilter.filter((id) => !superAdminIds.has(id));
          }
          console.log('[AdminUsers] Filtering to', userIdsFilter.length, 'users for tenant', currentTenantId);
        } else {
          setUsers([]);
          setRoles([]);
          setGroups([]);
          setLoading(false);
          return;
        }
      }

      // Fetch in parallel. Scope groups and roles by workspace so they never cross tenants.
      let usersQuery = supabase.from('users').select('*').order('created_at', { ascending: false });
      usersQuery = usersQuery.in('id', userIdsFilter);
      let groupsQuery = supabase.from('groups').select('*');
      let rolesQuery = supabase.from('roles').select('*');
      if (storeWorkspaceId) {
        groupsQuery = groupsQuery.eq('workspace_id', storeWorkspaceId);
        const tenantPart = storeTenantId ? `,workspace_id.eq.${storeTenantId}` : '';
        rolesQuery = rolesQuery.or(`is_system.eq.true,workspace_id.eq.${storeWorkspaceId},workspace_id.is.null${tenantPart}`);
      } else {
        groupsQuery = groupsQuery.eq('workspace_id', '00000000-0000-0000-0000-000000000000');
        rolesQuery = rolesQuery.eq('is_system', true);
      }
      // Fetch role assignments for workspace users and groups.
      // We filter by subject_id (not scope_id) so that org-unit-scoped assignments
      // (scope_id = some org unit UUID) are not silently dropped — any scope level
      // should appear on the Users page.  Cross-workspace contamination is prevented
      // because userIdsFilter was already scoped to this workspace.
      const safeSubjects = userIdsFilter.length > 0 ? userIdsFilter : ['00000000-0000-0000-0000-000000000000'];
      const raQuery = supabase
        .from('role_assignments')
        .select('*')
        .or(`subject_type.eq.group,subject_id.in.(${safeSubjects.join(',')})`)
        .eq('active', true);

      // Scope memberships by the already-workspace-filtered user IDs so no cross-tenant entries appear
      const safeUserIds = userIdsFilter.length > 0 ? userIdsFilter : ['00000000-0000-0000-0000-000000000000'];

      const [usersResult, rolesResult, groupsResult, roleAssignmentsResult, membershipsResult] = await Promise.all([
        usersQuery,
        rolesQuery,
        groupsQuery,
        raQuery,
        supabase.from('memberships')
          .select('id,user_id,group_id,created_at,groups:group_id(id,name)')
          .in('user_id', safeUserIds),
      ]);

      console.log('[AdminUsers] Fetch complete:', {
        users: usersResult.data?.length || 0,
        usersError: usersResult.error?.message,
        roles: rolesResult.data?.length || 0,
        groups: groupsResult.data?.length || 0,
        assignments: roleAssignmentsResult.data?.length || 0,
        memberships: membershipsResult.data?.length || 0,
      });

      if (usersResult.error) throw new Error(usersResult.error.message);

      // Build lookup maps
      const roleLookup = new Map<string, Role>();
      (rolesResult.data || []).forEach(role => {
        const tiers = role.tiers as number[] || [];
        const maxTier = tiers.length > 0 ? Math.max(...tiers) : 0;
        roleLookup.set(role.id, {
          id: role.id,
          label: role.display_name || role.name || 'Unknown Role',
          permissionTier: maxTier,
          description: role.description || '',
        });
      });

      // Backfill any role IDs referenced by assignments but missing from the workspace-scoped query
      // Every role id any assignment references — group assignments included.
      // Filtering to subject_type 'user' left a role inherited through a group
      // unresolved, and it fell through to the id-as-name path below.
      const assignedRoleIds = new Set(
        (roleAssignmentsResult.data || []).map(ra => ra.role_id as string)
      );
      const missingRoleIds = [...assignedRoleIds].filter(id => !roleLookup.has(id));
      if (missingRoleIds.length > 0) {
        const { data: extraRoles } = await supabase.from('roles').select('*').in('id', missingRoleIds);
        (extraRoles || []).forEach(role => {
          const tiers = role.tiers as number[] || [];
          const maxTier = tiers.length > 0 ? Math.max(...tiers) : 0;
          roleLookup.set(role.id, {
            id: role.id,
            label: role.display_name || role.name || 'Unknown Role',
            permissionTier: maxTier,
            description: role.description || '',
          });
        });
      }

      // Group role assignments by user (direct)
      const rolesByUser = new Map<string, RoleAssignment[]>();
      // Group role assignments by group (for inheritance)
      const rolesByGroup = new Map<string, { roleId: string; roleLabel: string; tier: number; scopeKind: string; scopeId: string }[]>();

      (roleAssignmentsResult.data || []).forEach(ra => {
        const roleInfo = roleLookup.get(ra.role_id);
        const entry = {
          roleId: ra.role_id,
          // Never print an id where a name belongs. A role we cannot resolve
          // says so; the id stays available in the chip's tooltip.
          roleLabel: roleInfo?.label || 'Unnamed role',
          tier: roleInfo?.permissionTier || 0,
          scopeKind: ra.scope_kind,
          scopeId: ra.scope_id,
        };

        if (ra.subject_type === 'user') {
          if (!rolesByUser.has(ra.subject_id)) rolesByUser.set(ra.subject_id, []);
          rolesByUser.get(ra.subject_id)!.push({ id: ra.id, ...entry });
        } else if (ra.subject_type === 'group') {
          if (!rolesByGroup.has(ra.subject_id)) rolesByGroup.set(ra.subject_id, []);
          rolesByGroup.get(ra.subject_id)!.push(entry);
        }
      });

      // Group memberships by user
      const membershipsByUser = new Map<string, GroupMembership[]>();
      // The select is `groups:group_id(id,name)`, which PostgREST returns as an
      // ARRAY of joined rows — so the old `m.groups?.name` was always undefined
      // and every membership rendered as "Unknown". `is_primary` is not selected
      // at all, so it was always undefined here too; it is read from the row
      // when present and false otherwise, rather than silently undefined.
      type MembershipRow = {
        id: string;
        user_id: string;
        group_id: string;
        created_at?: string | null;
        is_primary?: boolean | null;
        groups?: { id?: string | null; name?: string | null }[] | { name?: string | null } | null;
      };
      const groupNameOf = (g: MembershipRow['groups']): string => {
        if (!g) return 'Unknown';
        const row = Array.isArray(g) ? g[0] : g;
        return row?.name ?? 'Unknown';
      };
      (membershipsResult.data || []).forEach((m: MembershipRow) => {
        if (!membershipsByUser.has(m.user_id)) {
          membershipsByUser.set(m.user_id, []);
        }
        membershipsByUser.get(m.user_id)!.push({
          id: m.id,
          groupId: m.group_id,
          groupName: groupNameOf(m.groups),
          isPrimary: m.is_primary ?? false,
        });
      });

      // Build user list — compute inherited roles from group memberships
      const usersWithPermissions: UserWithPermissions[] = (usersResult.data || []).map(user => {
        const userGroups = membershipsByUser.get(user.id) || [];
        const inherited: InheritedRole[] = [];
        const directRoleIds = new Set((rolesByUser.get(user.id) || []).map(r => r.roleId));

        for (const gm of userGroups) {
          const groupRoles = rolesByGroup.get(gm.groupId) || [];
          for (const gr of groupRoles) {
            // Only show if not already held directly (avoids duplicates)
            if (!directRoleIds.has(gr.roleId)) {
              inherited.push({
                ...gr,
                viaGroupId: gm.groupId,
                viaGroupName: gm.groupName,
              });
            }
          }
        }

        const lastLoginAt: string | null = user.last_login_at ?? null;
        const lastSeenAt: string | null = user.last_seen_at ?? null;

        return {
          id: user.id,
          externalId: user.external_id,
          name: user.name,
          email: user.email,
          createdAt: user.created_at,
          lastLoginAt,
          lastSeenAt,
          activityStatus: computeActivityStatus(lastSeenAt),
          roles: rolesByUser.get(user.id) || [],
          inheritedRoles: inherited,
          groups: userGroups,
          isActive: true,
        };
      });

      setUsers(usersWithPermissions);
      setRoles(Array.from(roleLookup.values()));
      setGroups((groupsResult.data || []).map(g => ({
        id: g.id,
        name: g.name,
        type: g.type,
      })));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch when tenant or workspace changes (workspace-admin scoped by workspace, others by tenant)
  useEffect(() => {
    const hasContext = adminScope === 'workspace' ? storeWorkspaceId : storeTenantId;
    if (hasContext) {
      fetchData();
      fetchOrgUnits(); // Load org units for scope selection
    } else {
      // No tenant/workspace selected — clear list and stop loading so we don't show a perpetual spinner
      setUsers([]);
      setRoles([]);
      setGroups([]);
      setLoading(false);
    }
    // fetchData/fetchOrgUnits are stable for this effect's purpose: they are
    // recreated each render but only ever read the same context this dependency
    // list already tracks. Listing them would re-fetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeTenantId, storeWorkspaceId, adminScope, assignedRoleNames]);

  // ========================================================================
  // Helpers
  // ========================================================================

  /**
   * Resolve a scope id to something a person can read.
   *
   * The fallback used to be the first eight characters of the UUID, so every
   * role chip on every row read "@ d4cfe3a8…" — which is the workspace's own id,
   * never in orgUnitsMap, and meaningless to the reader. A scope that is the
   * workspace IS workspace-wide; anything else we cannot name says so plainly
   * and keeps the id in the tooltip for support, rather than printing half of it
   * as though it were a name.
   */
  const getScopeDisplayName = (_scopeKind: string | undefined | null, scopeId: string | undefined | null): string => {
    if (!scopeId || scopeId === '*') return 'Workspace-wide';
    if (scopeId === storeWorkspaceId) return 'Workspace-wide';
    const orgUnit = orgUnitsMap.get(scopeId);
    if (orgUnit) return orgUnit.displayName;
    return 'Scope not found';
  };
  
  /**
   * Get the current user's highest permission tier.
   * Users cannot assign roles with a higher tier than their own.
   * Platform/system admins get tier 5 (max).
   */
  const getCurrentUserMaxTier = (): number => {
    // Platform and system admins can assign any role
    if (currentRole === 'platform-admin' || currentRole === 'system-admin') {
      return 5;
    }
    
    // Find current user in the users list and get their max tier from role assignments
    const currentUser = users.find(u => u.id === currentUserId);
    if (!currentUser || currentUser.roles.length === 0) {
      return 0; // No roles = can't assign any roles
    }
    
    return Math.max(...currentUser.roles.map(r => r.tier));
  };
  
  /**
   * Filter roles to only show roles the current user can assign.
   * A user can only assign roles with permission tier <= their own highest tier.
   */
  const getAssignableRoles = (): Role[] => {
    const maxTier = getCurrentUserMaxTier();
    return roles.filter(role => role.permissionTier <= maxTier);
  };

  // ========================================================================
  // Actions
  // ========================================================================

  const assignRole = async (userId: string, roleId: string, scopeKind: ScopeKind, scopeId: string) => {
    try {
      const result = await createRoleAssignmentRpc({
        subjectType: 'user',
        subjectId: userId,
        roleId,
        scopeKind,
        scopeId,
      });
      if (!result.success) throw new Error(result.error);
      await fetchData();
      setShowRoleModal(false);
    } catch (err) {
      alert(`Failed to assign role: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const removeRoleAssignment = async (assignmentId: string) => {
    if (!confirm('Remove this role assignment?')) return;
    
    try {
      const result = await deleteRoleAssignmentRpc(assignmentId);
      if (!result.success) throw new Error(result.error);
      await fetchData();
    } catch (err) {
      alert(`Failed to remove role: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const addToGroup = async (userId: string, groupId: string) => {
    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.from('memberships').insert({
        user_id: userId,
        group_id: groupId,
      });
      
      if (error) throw error;
      await fetchData();
      setShowGroupModal(false);
    } catch (err) {
      alert(`Failed to add to group: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const removeFromGroup = async (membershipId: string) => {
    if (!confirm('Remove from this group?')) return;
    
    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.from('memberships').delete().eq('id', membershipId);
      if (error) throw error;
      await fetchData();
    } catch (err) {
      alert(`Failed to remove from group: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleDeleteUser = async (user: UserWithPermissions) => {
    if (!confirm(`Are you sure you want to delete user "${user.name}"? This action cannot be undone.`)) return;
    
    try {
      const supabase = getSupabaseClient();
      
      // Delete all related records first (foreign key constraints)
      
      // 1. Delete role assignments
      const raResult = await deleteRoleAssignmentsForSubjectRpc(user.id);
      if (!raResult.success) console.warn('[AdminUsers] Failed to delete role_assignments:', raResult.error);
      
      // 2. Delete group memberships
      const { error: membershipError } = await supabase.from('memberships').delete().eq('user_id', user.id);
      if (membershipError) console.warn('[AdminUsers] Failed to delete memberships:', membershipError.message);
      
      // 3. Delete tenant memberships
      const { error: tenantError } = await supabase.from('tenant_memberships').delete().eq('user_id', user.id);
      if (tenantError) console.warn('[AdminUsers] Failed to delete tenant_memberships:', tenantError.message);
      
      // 4. Delete workspace memberships
      const { error: workspaceError } = await supabase.from('workspace_memberships').delete().eq('user_id', user.id);
      if (workspaceError) console.warn('[AdminUsers] Failed to delete workspace_memberships:', workspaceError.message);
      
      // 5. Delete cycle participants
      const { error: cycleParticipantError } = await supabase.from('cycle_participants').delete().eq('user_id', user.id);
      if (cycleParticipantError) console.warn('[AdminUsers] Failed to delete cycle_participants:', cycleParticipantError.message);
      
      // Finally, delete the user via SECURITY DEFINER RPC (no DELETE policy on public.users).
      const { error } = await supabase.rpc('delete_user', { target_user_uuid: user.id });
      if (error) throw error;
      
      await fetchData();
    } catch (err) {
      alert(`Failed to delete user: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleImpersonate = async (user: UserWithPermissions) => {
    if (adminScope !== 'platform') {
      return; // Only platform admins may impersonate; button is hidden for others
    }
    try {
      const targetIsPlatformAdmin = await isSuperAdminBySupabaseUserId(user.id);
      if (targetIsPlatformAdmin && adminScope !== 'platform') {
        alert('You cannot impersonate a platform administrator.');
        return;
      }
      // Workspace admins cannot impersonate tenant admins
      if (adminScope === 'workspace') {
        const supabase = getSupabaseClient();
        const { data: targetRoles } = await supabase
          .from('role_assignments')
          .select('roles(name)')
          .eq('subject_type', 'user')
          .eq('subject_id', user.id)
          .eq('active', true);
        const roleNames: string[] = [];
        (targetRoles || []).forEach((r: { roles?: { name: string } | { name: string }[] | null }) => {
          const role = r.roles;
          if (role && typeof role === 'object' && 'name' in role && typeof (role as { name: string }).name === 'string') {
            roleNames.push((role as { name: string }).name);
          } else if (Array.isArray(role)) {
            role.forEach((item) => item?.name && roleNames.push(item.name));
          }
        });
        const isTenantAdmin = roleNames.some((name) => name === 'tenant-admin' || name === 'tenant_admin');
        if (isTenantAdmin) {
          alert('You cannot impersonate a tenant administrator.');
          return;
        }
      }
      // Get the user's primary role from role_assignments
      const supabase = getSupabaseClient();
      const { data: assignments } = await supabase
        .from('role_assignments')
        .select('role_id, roles:role_id(name)')
        .eq('subject_type', 'user')
        .eq('subject_id', user.id)
        .eq('active', true)
        .limit(1);
      
      const roleName =
        (assignments?.[0]?.roles as { name?: string | null } | null)?.name || 'strategist';
      
      // Start impersonation - this will load the impersonated user's permissions
      startImpersonation(user.id, user.name, user.email, roleName);
      
      // Navigate to home to see the app as the impersonated user
      window.location.href = '/prioritization';
    } catch (err) {
      console.error('[AdminUsers] Impersonation error:', err);
      alert(`Failed to impersonate: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleUpdateUser = async (userId: string, updates: { name: string; email: string }) => {
    try {
      const supabase = getSupabaseClient();
      // The server decides who may change this person, and a refusal comes back
      // as a sentence. A direct write to their row is refused without an error.
      const { error } = await supabase.rpc('update_person_details', {
        p_user_id: userId,
        p_name: updates.name,
        p_email: updates.email,
      });

      if (error) throw new Error(error.message);

      setShowEditUserModal(false);
      setEditingUser(null);
      await fetchData();
    } catch (err) {
      alert(`Failed to update user: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // ========================================================================
  // Filtering
  // ========================================================================

  const filteredUsers = users.filter(user => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.name.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query)
    );
  });

  // ========================================================================
  // Permission Check — allow tenant-admin and workspace-admin (scoped); platform-admin (full)
  // ========================================================================

  if (adminScope === 'none') {
    return (
      <div className="p-8">
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-4">
          <h2 className="text-lg font-semibold text-[var(--coral)] mb-2">Access Denied</h2>
          <p className="text-sm text-[var(--coral)] mb-4">
            You must be a Tenant Admin, Workspace Admin, or Platform Admin to access this page.
          </p>
          <button
            onClick={() => switchRole('platform-admin')}
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)]"
          >
            Switch to Platform Admin
          </button>
        </div>
      </div>
    );
  }

  // ========================================================================
  // Render
  // ========================================================================

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={<Users className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Users & Permissions"
        subtitle="Manage users, roles, and group memberships"
        actions={
          <div className="flex gap-2">
            <button onClick={() => setShowCreateUserModal(true)} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors">
              <UserPlus className="h-4 w-4" />
              Create User
            </button>
            <button onClick={() => setShowInviteModal(true)} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors">
              <Plus className="h-4 w-4" />
              Add user directly
            </button>
          </div>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="text-sm text-[var(--text-ter)]">Total Users</div>
          <div className="text-2xl font-bold text-[var(--text-pri)]">{users.length}</div>
        </div>
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="text-sm text-[var(--text-ter)]">With Roles</div>
          <div className="text-2xl font-bold text-[var(--text-pri)]">
            {users.filter(u => u.roles.length > 0).length}
          </div>
        </div>
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="text-sm text-[var(--text-ter)]">In Groups</div>
          <div className="text-2xl font-bold text-[var(--text-ter)]">
            {users.filter(u => u.groups.length > 0).length}
          </div>
        </div>
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="text-sm text-[var(--text-ter)]">Active (30d)</div>
          <div className="text-2xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">
            {users.filter(u => u.activityStatus === 'active').length}
          </div>
          <div className="text-xs text-[var(--text-ter)] mt-1">
            {users.filter(u => u.activityStatus === 'never_logged_in').length} never logged in
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[var(--text-ter)]" />
        <input
          type="text"
          placeholder="Search users by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-4">
          <p className="text-[var(--coral)]">{error}</p>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
        <table className="min-w-full divide-y divide-[var(--rule)]">
          <thead className="bg-[var(--surface)] border-b border-[var(--rule)]">
            <tr>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">User</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Activity</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Roles</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Groups</th>
              <th className="px-4 py-2.5 text-right text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-[var(--bg-raised)] divide-y divide-[var(--rule)]">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-[var(--text-ter)]">
                  <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                  Loading users...
                </td>
              </tr>
            ) : filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-[var(--text-ter)]">
                  {searchQuery ? 'No users match your search.' : 'No users found.'}
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-[var(--surface)] align-top">
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-[var(--hover-tint)] flex items-center justify-center shrink-0">
                        <span className="text-sm font-medium text-[var(--text-sec)]">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-[var(--text-pri)] leading-tight">{user.name}</div>
                        <div className="text-xs text-[var(--text-ter)] leading-tight">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap align-top">
                    {user.activityStatus === 'active' && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Active
                      </span>
                    )}
                    {user.activityStatus === 'idle' && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[var(--mustard-dim)] text-[var(--mustard)]">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Idle
                      </span>
                    )}
                    {user.activityStatus === 'inactive' && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[var(--coral-dim)] text-[var(--coral)]">
                        <XCircle className="h-3 w-3 mr-1" />
                        Inactive
                      </span>
                    )}
                    {user.activityStatus === 'never_logged_in' && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[var(--hover-tint)] text-[var(--text-sec)]">
                        <XCircle className="h-3 w-3 mr-1" />
                        Never logged in
                      </span>
                    )}
                    <div className="text-xs text-[var(--text-ter)] mt-1">
                      {formatLastLogin(user.lastLoginAt)}
                    </div>
                  </td>
                  <td className="px-4 py-3 max-w-xs">
                    {user.roles.length === 0 && user.inheritedRoles.length === 0 ? (
                      <span className="text-xs text-[var(--text-ter)] italic">No roles</span>
                    ) : (() => {
                      const allDirect = user.roles;
                      const isExpanded = expandedRoleUsers.has(user.id);
                      const showAll = isExpanded || allDirect.length <= ROLES_COLLAPSE_AFTER;
                      const visible = showAll ? allDirect : allDirect.slice(0, ROLES_COLLAPSE_AFTER);
                      const hiddenCount = allDirect.length - ROLES_COLLAPSE_AFTER;
                      return (
                        <div className="flex flex-wrap gap-1">
                          {visible.map((role) => (
                            <span
                              key={role.id}
                              className="inline-flex items-center gap-1 border border-[var(--rule)] bg-[var(--surface)] px-1.5 py-0.5 text-xs text-[var(--text-sec)] group"
                            >
                              <Shield className="h-2.5 w-2.5 text-[var(--text-ter)] shrink-0" />
                              <span className="font-medium leading-none">{role.roleLabel}</span>
                              <span className="text-[var(--text-ter)] leading-none">@&nbsp;{getScopeDisplayName(role.scopeKind, role.scopeId)}</span>
                              <button
                                onClick={() => setEditingRoleAssignment(role)}
                                className="text-[var(--text-ter)] hover:text-[var(--text-ter)] transition-colors"
                                title="Edit scope"
                              >
                                <Edit className="h-2.5 w-2.5" />
                              </button>
                              <button
                                onClick={() => removeRoleAssignment(role.id)}
                                className="text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors"
                                title="Remove"
                              >
                                <X className="h-2.5 w-2.5" />
                              </button>
                            </span>
                          ))}

                          {/* Inherited via group */}
                          {(showAll || allDirect.length === 0) && user.inheritedRoles.map((role, i) => (
                            <span
                              key={`${role.viaGroupId}-${role.roleId}-${i}`}
                              className="inline-flex items-center gap-1 border border-[var(--slate)] bg-[var(--slate-dim)] px-1.5 py-0.5 text-xs text-[var(--text-sec)]"
                              title={`Via group: ${role.viaGroupName}`}
                            >
                              <Shield className="h-2.5 w-2.5 text-[var(--text-ter)] shrink-0" />
                              <span className="leading-none">{role.roleLabel}</span>
                              <span className="text-[var(--text-sec)] leading-none">@&nbsp;{getScopeDisplayName(role.scopeKind, role.scopeId)}</span>
                              <Users className="h-2.5 w-2.5 text-[var(--text-ter)] shrink-0" title={role.viaGroupName} />
                            </span>
                          ))}

                          {/* Collapse / expand toggle */}
                          {!showAll && hiddenCount > 0 && (
                            <button
                              onClick={() => setExpandedRoleUsers((prev) => { const next = new Set(prev); next.add(user.id); return next; })}
                              className="inline-flex items-center border border-[var(--rule)] bg-[var(--bg-raised)] px-1.5 py-0.5 text-xs text-[var(--text-ter)] hover:bg-[var(--surface)] hover:text-[var(--text-sec)]"
                            >
                              +{hiddenCount} more
                            </button>
                          )}
                          {isExpanded && allDirect.length > ROLES_COLLAPSE_AFTER && (
                            <button
                              onClick={() => setExpandedRoleUsers((prev) => { const next = new Set(prev); next.delete(user.id); return next; })}
                              className="inline-flex items-center border border-[var(--rule)] bg-[var(--bg-raised)] px-1.5 py-0.5 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                            >
                              Show less
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3">
                    {user.groups.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {user.groups.map((group) => (
                          <span key={group.id} className="inline-flex items-center gap-1 border border-[var(--slate)] bg-[var(--slate-dim)] px-1.5 py-0.5 text-xs text-[var(--text-sec)]">
                            <Users className="h-2.5 w-2.5 text-[var(--text-ter)] shrink-0" />
                            <span className="leading-none">{group.groupName}</span>
                            {group.isPrimary && (
                              <span className="text-[var(--text-sec)] leading-none">·&nbsp;Primary</span>
                            )}
                            <button
                              onClick={() => removeFromGroup(group.id)}
                              className="text-[var(--text-sec)] hover:text-[var(--coral)] transition-colors"
                            >
                              <X className="h-2.5 w-2.5" />
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-[var(--text-ter)] italic">No groups</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => { setSelectedUser(user); setShowAssignModal(true); }}
                        className="inline-flex items-center gap-1 border border-[var(--rule)] px-2 py-1 text-xs font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] hover:border-[var(--rule)]"
                        title="Assign roles, access, or packages"
                      >
                        <Shield className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                        Assign
                      </button>
                      <Link
                        to={`/prioritization/settings/users/${user.id}/permissions`}
                        className="text-[var(--text-ter)] hover:text-[var(--text-sec)] p-1"
                        title="View permissions report"
                      >
                        <Shield className="h-4 w-4" />
                      </Link>
                      {(adminScope === 'platform' || user.id === currentUserId || !hasSignIn(user)) && (
                        <button
                          onClick={() => { setEditingUser(user); setShowEditUserModal(true); }}
                          className="text-[var(--text-sec)] hover:text-[var(--text-pri)] p-1"
                          title="Edit User"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      {adminScope === 'platform' && isImpersonationEnabled() && (
                        <button
                          onClick={() => handleImpersonate(user)}
                          className="text-[var(--mustard)] hover:text-[var(--mustard)] p-1"
                          title="Impersonate User"
                          disabled={user.id === currentUserId}
                        >
                          <UserCheck className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => setResetPasswordUser(user)}
                        className="text-[var(--text-ter)] hover:text-[var(--text-pri)] p-1"
                        title="Reset Password"
                      >
                        <KeyRound className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user)}
                        className="text-[var(--coral)] hover:text-[var(--coral)] p-1"
                        title="Delete User"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Unified Assign Roles Modal */}
      {showAssignModal && selectedUser && (() => {
        const assignTarget = users.find((u) => u.id === selectedUser.id) ?? selectedUser;
        const isWorkspaceAdmin = assignTarget.roles.some((r) =>
          /workspace[\s-]?admin/i.test(r.roleLabel) || /^admin$/i.test(r.roleLabel),
        );
        return (
          <AssignRoleModal
            user={assignTarget}
            workspaceId={storeWorkspaceId}
            currentUserId={currentUserId}
            heldRoleIds={assignTarget.roles.map((r) => r.roleId)}
            isWorkspaceAdmin={isWorkspaceAdmin}
            orgUnits={orgUnitsList}
            onSuccess={() => void fetchData()}
            onClose={() => { setShowAssignModal(false); setSelectedUser(null); }}
          />
        );
      })()}

      {/* Role Assignment Modal (kept as internal fallback for other entry points) */}
      {showRoleModal && selectedUser && (
        <RoleAssignmentModal
          user={selectedUser}
          roles={getAssignableRoles()}
          orgUnits={orgUnitsList}
          onAssign={assignRole}
          onClose={() => { setShowRoleModal(false); setSelectedUser(null); }}
        />
      )}

      {/* Group Assignment Modal (kept as internal fallback for other entry points) */}
      {showGroupModal && selectedUser && (
        <GroupAssignmentModal
          user={selectedUser}
          groups={groups}
          workspaceId={storeWorkspaceId}
          onAssign={addToGroup}
          onClose={() => { setShowGroupModal(false); setSelectedUser(null); }}
        />
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <InviteUserModal
          roles={getAssignableRoles()}
          groups={groups}
          onInvite={async (data) => {
            try {
              const supabase = getSupabaseClient();
              
              // Use Zustand store values (the single source of truth for workspace context)
              const currentWorkspaceId = storeWorkspaceId;
              
              if (!currentWorkspaceId) {
                throw new Error('No workspace selected. Choose a workspace from the sidebar first.');
              }
              
              // The person, their tenant membership and their workspace membership
              // are written by the server, which checks the caller administers
              // this workspace (migration 997).
              const { data: newUserId, error: addError } = await supabase.rpc('add_person_without_login', {
                p_workspace_id: currentWorkspaceId,
                p_name: data.name,
                p_email: data.email,
              });
              
              if (addError || !newUserId) {
                console.error('[AdminUsers] Adding the person failed:', addError);
                throw new Error(`Failed to add user: ${addError?.message ?? 'no person was returned'}`);
              }
              
              const newUser = { id: newUserId as string, name: data.name, email: data.email };
              
              // Assign initial role if specified
              if (data.roleId && newUser) {
                const raResult = await createRoleAssignmentRpc({
                  subjectType: 'user',
                  subjectId: newUser.id,
                  roleId: data.roleId,
                  scopeKind: 'global',
                  scopeId: '*',
                });
                if (!raResult.success) {
                  console.warn('[AdminUsers] Role assignment failed:', raResult.error);
                } else {
                  console.log('[AdminUsers] Assigned role:', data.roleId);
                }
              }
              
              // Add to group if specified
              if (data.groupId && newUser) {
                const { error: groupError } = await supabase.from('memberships').insert({
                  user_id: newUser.id,
                  group_id: data.groupId,
                  role: 'member',
                });
                
                if (groupError) {
                  console.warn('[AdminUsers] Group membership failed:', groupError);
                } else {
                  console.log('[AdminUsers] Added to group:', data.groupId);
                }
              }
              
              console.log('[AdminUsers] User creation complete:', newUser.name);
              
              // SUCCESS: Show notification
              addNotification({
                type: 'success',
                title: 'User Created',
                message: `${newUser.name} (${newUser.email}) has been added successfully.`,
                duration: 5000,
              });
              
              // Refresh data to show new user
              await fetchData();
              setShowInviteModal(false);
              
              // Clear any previous errors
              setError(null);
            } catch (err) {
              console.error('[AdminUsers] Error inviting user:', err);
              const errorMessage = err instanceof Error ? err.message : 'Failed to invite user';
              
              // CRITICAL: Show error prominently via notification AND alert AND state
              addNotification({
                type: 'error',
                title: 'User Creation Failed',
                message: errorMessage,
                duration: 10000, // Show longer for errors
              });
              
              setError(errorMessage);
              
              // Keep modal open so user can see the error and retry
              alert(`DATABASE ERROR: ${errorMessage}\n\nCheck the browser console for details.`);
            }
          }}
          onClose={() => setShowInviteModal(false)}
        />
      )}

      {/* Edit Role Assignment Modal */}
      {editingRoleAssignment && (
        <EditRoleAssignmentModal
          assignment={editingRoleAssignment}
          orgUnits={orgUnitsList}
          onSave={async (assignmentId, updates) => {
            try {
              await updateRoleAssignment(assignmentId, updates);
              setEditingRoleAssignment(null);
              fetchData();
            } catch (err) {
              alert(`Failed to update: ${err instanceof Error ? err.message : 'Unknown error'}`);
            }
          }}
          onClose={() => setEditingRoleAssignment(null)}
        />
      )}

      {/* Edit User Modal */}
      {showEditUserModal && editingUser && (
        <EditUserModal
          user={editingUser}
          onSave={handleUpdateUser}
          onClose={() => { setShowEditUserModal(false); setEditingUser(null); }}
        />
      )}

      {/* Reset Password Modal */}
      {resetPasswordUser && (
        <ResetPasswordModal
          user={resetPasswordUser}
          onReset={async (newPassword) => {
            await resetUserPassword({ targetUserId: resetPasswordUser.id, newPassword });
            addNotification({
              type: 'success',
              title: 'Password Reset',
              message: `Password updated for ${resetPasswordUser.name}.`,
              duration: 4000,
            });
            setResetPasswordUser(null);
          }}
          onClose={() => setResetPasswordUser(null)}
        />
      )}

      {/* Create User Modal */}
      {showCreateUserModal && (
        <CreateUserModal
          roles={getAssignableRoles()}
          groups={groups}
          orgUnits={orgUnitsList}
          tenantId={storeTenantId ?? ''}
          workspaceId={storeWorkspaceId ?? ''}
          createdBy={currentUserId ?? ''}
          cloneCandidates={users
            .filter((u) => u.activityStatus !== 'never_logged_in')
            .map((u) => ({ id: u.id, name: u.name, email: u.email }))}
          onSuccess={async (newUserId, assignment, meta) => {
            const supabase = getSupabaseClient();

            if (assignment?.mode === 'package') {
              // Apply all slugs from the package with their tiers
              for (const slug of assignment.pkg.roleSlug) {
                const { data: roleRow } = await supabase
                  .from('roles').select('id').eq('name', slug).maybeSingle();
                if (!roleRow?.id) { console.warn('[AdminUsers] Package role not found:', slug); continue; }
                const tier = getRoleSlugTier(assignment.pkg, slug);
                const raResult = await createRoleAssignmentRpc({
                  subjectType: 'user',
                  subjectId: newUserId,
                  roleId: roleRow.id,
                  scopeKind: 'global',
                  scopeId: storeWorkspaceId ?? '*',
                  tier,
                });
                if (!raResult.success) console.warn('[AdminUsers] Package role assignment failed:', raResult.error);
              }
            } else if (assignment?.mode === 'group') {
              await applyGroupMemberships(newUserId, [assignment.groupId]);
            } else if (assignment?.mode === 'roles') {
              await applyIndividualRoleAssignments(
                newUserId,
                assignment.roleIds.map((roleId) => ({
                  roleId,
                  scopeKind: assignment.scopeOrgUnitId ? 'resource' : 'global',
                  scopeId: assignment.scopeOrgUnitId || storeWorkspaceId || '*',
                })),
              );
            }

            addNotification({
              type: 'success',
              title: meta?.linkedExisting ? 'User added to workspace' : 'User Created',
              message: meta?.linkedExisting
                ? 'They already had an account; membership and selected roles were applied.'
                : 'The account is ready. They can log in at /auth/login.',
              duration: 5000,
            });
            await fetchData();
            setShowCreateUserModal(false);
          }}
          onClose={() => setShowCreateUserModal(false)}
        />
      )}
    </div>
  );
}

// ============================================================================
// Edit Role Assignment Modal
// ============================================================================

interface EditRoleAssignmentModalProps {
  assignment: RoleAssignment;
  orgUnits: OrgUnit[];
  onSave: (assignmentId: string, updates: { scopeKind: ScopeKind; scopeId: string }) => void;
  onClose: () => void;
}

function EditRoleAssignmentModal({ assignment, orgUnits, onSave, onClose }: EditRoleAssignmentModalProps) {
  // Determine initial selection: '*' (workspace-wide) or a specific org unit ID
  const isWorkspaceWide =
    !assignment.scopeId ||
    assignment.scopeId === '*' ||
    assignment.scopeKind === 'organization' ||
    assignment.scopeKind === 'global';

  const [orgUnitId, setOrgUnitId] = useState<string>(isWorkspaceWide ? '*' : (assignment.scopeId || '*'));

  const handleSubmit = () => {
    if (orgUnitId === '*') {
      onSave(assignment.id, { scopeKind: 'organization', scopeId: '*' });
    } else {
      onSave(assignment.id, { scopeKind: 'resource', scopeId: orgUnitId });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-[var(--text-pri)]">Edit Role Scope</h2>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-4 p-3 bg-[var(--surface)]">
          <div className="text-sm text-[var(--text-sec)]">Role</div>
          <div className="font-medium">{assignment.roleLabel}</div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Where does this role apply?</label>
            <select
              value={orgUnitId}
              onChange={(e) => setOrgUnitId(e.target.value)}
              className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
            >
              <option value="*">Workspace-wide (all org units)</option>
              {orgUnits.map(o => (
                <option key={o.id} value={o.id}>
                  {'  '.repeat(o.depth ?? 0)}{o.displayName}
                  {o.type ? ` (${o.type})` : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-[var(--text-ter)] mt-1">
              Select an org unit to restrict this role to that node and its descendants (per the role's cascade setting), or choose workspace-wide for unrestricted access.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)]"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)]"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Role Assignment Modal
// ============================================================================

interface RoleAssignmentModalProps {
  user: UserWithPermissions;
  roles: Role[];
  orgUnits: OrgUnit[];
  onAssign: (userId: string, roleId: string, scopeKind: ScopeKind, scopeId: string) => void;
  onClose: () => void;
}

function RoleAssignmentModal({ user, roles, orgUnits, onAssign, onClose }: RoleAssignmentModalProps) {
  const [selectedRole, setSelectedRole] = useState('');
  // '*' means workspace-wide; any other value is an org unit ID
  const [orgUnitId, setOrgUnitId] = useState<string>('*');

  const handleSubmit = () => {
    if (!selectedRole) {
      alert('Please select a role');
      return;
    }
    // Map to DB columns: workspace-wide → organization scope; specific node → resource scope
    if (orgUnitId === '*') {
      onAssign(user.id, selectedRole, 'organization', '*');
    } else {
      onAssign(user.id, selectedRole, 'resource', orgUnitId);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-[var(--text-pri)]">Assign Role to {user.name}</h2>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Role</label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a role...</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>

          {/* Org Unit Scope */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Where does this role apply?
            </label>
            <select
              value={orgUnitId}
              onChange={(e) => setOrgUnitId(e.target.value)}
              className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
            >
              <option value="*">Workspace-wide (all org units)</option>
              {orgUnits.map(o => (
                <option key={o.id} value={o.id}>
                  {'  '.repeat(o.depth ?? 0)}{o.displayName}
                  {o.type ? ` (${o.type})` : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-[var(--text-ter)] mt-1">
              The role's cascade setting controls whether access extends to child org units.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)]"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedRole}
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)]"
          >
            Assign Role
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Group Assignment Modal
// ============================================================================

interface GroupAssignmentModalProps {
  user: UserWithPermissions;
  groups: Group[];
  workspaceId: string | null;
  onAssign: (userId: string, groupId: string) => void;
  onClose: () => void;
}

function GroupAssignmentModal({ user, groups, workspaceId, onAssign, onClose }: GroupAssignmentModalProps) {
  const [selectedGroup, setSelectedGroup] = useState('');
  const [localGroups, setLocalGroups] = useState<Group[]>(groups);

  // Filter out groups user is already in
  const existingGroupIds = new Set(user.groups.map(g => g.groupId));
  const availableGroups = localGroups.filter(g => !existingGroupIds.has(g.id));

  const handleGroupCreated = (created: { id: string; name: string; displayName: string }) => {
    const newGroup: Group = { id: created.id, name: created.displayName, type: 'custom' };
    setLocalGroups(prev => [...prev, newGroup]);
    setSelectedGroup(created.id);
  };

  const handleSubmit = () => {
    if (!selectedGroup) {
      alert('Please select a group');
      return;
    }
    onAssign(user.id, selectedGroup);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-[var(--text-pri)]">Add {user.name} to Group</h2>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Group</label>
            {availableGroups.length === 0 ? (
              <p className="text-sm text-[var(--text-ter)] italic">User is already in all groups, or no groups exist yet.</p>
            ) : (
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a group...</option>
                {availableGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            )}
            <InlineGroupCreator workspaceId={workspaceId} onCreated={handleGroupCreated} />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)]"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedGroup || availableGroups.length === 0}
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)]"
          >
            Add to Group
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Invite User Modal
// ============================================================================

interface InviteUserData {
  name: string;
  email: string;
  roleId?: string;
  groupId?: string;
}

interface InviteUserModalProps {
  roles: Role[];
  groups: Group[];
  onInvite: (data: InviteUserData) => void;
  onClose: () => void;
}

function InviteUserModal({ roles, groups, onInvite, onClose }: InviteUserModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim() || !email.trim()) {
      alert('Name and email are required');
      return;
    }
    
    // Basic email validation
    if (!email.includes('@') || !email.includes('.')) {
      alert('Please enter a valid email address');
      return;
    }
    
    setSending(true);
    try {
      await onInvite({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        roleId: selectedRole || undefined,
        groupId: selectedGroup || undefined,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-[var(--text-pri)]">Add User Directly</h2>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Full Name <span className="text-[var(--coral)]">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Smith"
              className="w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Email Address <span className="text-[var(--coral)]">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john@company.com"
              className="w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Initial Role (Optional) */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Initial Role <span className="text-[var(--text-ter)] font-normal">(optional)</span>
            </label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
            >
              <option value="">No role assigned</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>

          {/* Initial Group (Optional) */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Initial Group <span className="text-[var(--text-ter)] font-normal">(optional)</span>
            </label>
            <select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
            >
              <option value="">No group assigned</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name} ({group.type})
                </option>
              ))}
            </select>
          </div>

          {/* Info Box */}
          <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-3">
            <p className="text-sm text-[var(--text-sec)]">
              This creates the user record and workspace membership directly — no email is sent
              and there is no link for them to accept.{' '}
              <Link to="/prioritization/settings/invites" className="underline font-medium" onClick={onClose}>
                Send an invitation instead
              </Link>{' '}
              if you want them to receive an email and accept on their own.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)]"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!name.trim() || !email.trim() || sending}
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] flex items-center gap-2"
          >
            {sending ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Adding...
              </>
            ) : (
              <>
                <UserPlus className="h-4 w-4" />
                Add User
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Create User Modal
// ============================================================================

/** Minimal shape needed to search/display candidate users to clone from. */
interface CloneCandidate {
  id: string;
  name: string;
  email: string;
}

interface CreateUserModalProps {
  roles: Role[];
  groups: Group[];
  orgUnits: OrgUnit[];
  tenantId: string;
  workspaceId: string;
  createdBy: string;
  /** Users eligible as a clone source (already scoped/filtered by the parent). */
  cloneCandidates: CloneCandidate[];
  onSuccess: (newUserId: string, assignment: PendingAssignment, meta?: { linkedExisting?: boolean }) => Promise<void>;
  onClose: () => void;
}

type CreateAssignMode = 'package' | 'group' | 'roles';

/** Toggleable chip for a single cloned item in the clone preview panel. */
function CloneBadge({
  kind,
  label,
  active,
  onToggle,
}: {
  kind: string;
  label: string;
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      title={`${kind}: ${label} — click to ${active ? 'remove' : 're-add'}`}
      className={`inline-flex items-center gap-1.5  border px-1.5 py-0.5 text-xs transition-colors ${
        active
          ? 'border-[var(--rule)] bg-[var(--surface)] text-[var(--text-sec)] hover:border-[var(--rule)]'
          : 'border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-ter)] line-through hover:border-[var(--rule)] hover:text-[var(--text-ter)]'
      }`}
    >
      {active && (
        <span className="font-mono text-[10px] uppercase tracking-wide text-[var(--text-ter)]">Cloned</span>
      )}
      <span className="leading-none">{label}</span>
      {active ? (
        <X className="h-2.5 w-2.5 text-[var(--text-ter)] shrink-0" />
      ) : (
        <Plus className="h-2.5 w-2.5 text-[var(--text-ter)] shrink-0" />
      )}
    </button>
  );
}

function CreateUserModal({ roles, groups, orgUnits, tenantId, workspaceId, createdBy, cloneCandidates, onSuccess, onClose }: CreateUserModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [creating, setCreating] = useState(false);
  const [linkingExisting, setLinkingExisting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdEmail, setCreatedEmail] = useState<string | null>(null);

  // Assignment selector state
  const [assignMode, setAssignMode] = useState<CreateAssignMode>('package');
  const [pkgQuery, setPkgQuery] = useState('');
  const [selectedPkg, setSelectedPkg] = useState<RolePackageDefinition | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [selectedOrgUnitId, setSelectedOrgUnitId] = useState('');
  const [roleQuery, setRoleQuery] = useState('');

  // Org chart placement
  const [selectedOrgChartNodeId, setSelectedOrgChartNodeId] = useState('');

  // --------------------------------------------------------------------
  // Clone-on-create state
  //
  // Cloning pre-populates the tabs above (assignMode/selectedPkg/etc.) when
  // a package is inferred, but individual roles and groups are applied
  // through a separate "extra" path (cloneApplyGroupIds/cloneApplyRoleIds)
  // rather than being forced into the single-select Group/Individual-Roles
  // tabs. Reasoning: the source user may hold multiple groups and roles at
  // different scopes, but this modal's existing tabs only support one group
  // and one shared scope for all individually-picked roles at a time. Routing
  // cloned items through a dedicated, always-applied list — shown in the
  // preview below with per-item toggles — preserves everything (including
  // each role's original scope) without needing to redesign the tabs or the
  // single-mode PendingAssignment contract.
  // --------------------------------------------------------------------
  const [cloneEnabled, setCloneEnabled] = useState(false);
  const [cloneSourceUserId, setCloneSourceUserId] = useState('');
  const [cloneLoading, setCloneLoading] = useState(false);
  const [cloneError, setCloneError] = useState<string | null>(null);
  const [cloneSourceAssignments, setCloneSourceAssignments] = useState<ClonedUserAssignments | null>(null);
  const [cloneApplyGroupIds, setCloneApplyGroupIds] = useState<Set<string>>(new Set());
  const [cloneApplyRoleIds, setCloneApplyRoleIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!cloneEnabled || !cloneSourceUserId) {
      setCloneSourceAssignments(null);
      setCloneApplyGroupIds(new Set());
      setCloneApplyRoleIds(new Set());
      setCloneError(null);
      setCloneLoading(false);
      return;
    }
    let cancelled = false;
    setCloneLoading(true);
    setCloneError(null);
    fetchUserAssignments(cloneSourceUserId)
      .then((result) => {
        if (cancelled) return;
        setCloneSourceAssignments(result);
        setCloneApplyGroupIds(new Set(result.groupIds));
        setCloneApplyRoleIds(new Set(result.individualRoles.map((r) => r.roleId)));
        // Clear any pre-population left over from a previously-selected clone
        // source before applying this one's data, so switching sources never
        // leaves stale selections behind.
        setSelectedPkg(null);
        setSelectedOrgChartNodeId('');
        if (result.packageSlug) {
          const pkg = getRolePackage(result.packageSlug);
          if (pkg) {
            setAssignMode('package');
            setSelectedPkg(pkg);
          }
        }
        if (result.orgUnitId) {
          setSelectedOrgChartNodeId(result.orgUnitId);
        }
      })
      .catch((err) => {
        if (!cancelled) setCloneError(err instanceof Error ? err.message : "Failed to load that user's configuration");
      })
      .finally(() => {
        if (!cancelled) setCloneLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cloneEnabled, cloneSourceUserId]);

  const toggleRole = (roleId: string) => {
    setSelectedRoleIds(prev =>
      prev.includes(roleId) ? prev.filter(id => id !== roleId) : [...prev, roleId]
    );
  };

  function buildAssignment(): PendingAssignment {
    if (assignMode === 'package' && selectedPkg) return { mode: 'package', pkg: selectedPkg };
    if (assignMode === 'group' && selectedGroupId) return { mode: 'group', groupId: selectedGroupId };
    if (assignMode === 'roles' && selectedRoleIds.length > 0) return { mode: 'roles', roleIds: selectedRoleIds, scopeOrgUnitId: selectedOrgUnitId };
    return null;
  }

  /**
   * Apply cloned groups/roles that fall outside the single-mode tab selection
   * above (see comment on the clone state block). Applied in addition to
   * whatever buildAssignment() produces — never in place of it — and skipped
   * entirely when cloning is off, so the default create flow is untouched.
   */
  const applyClonedExtras = async (userId: string) => {
    if (!cloneEnabled || !cloneSourceAssignments) return;

    // Avoid a duplicate membership insert if the admin also picked the same
    // group manually via the primary Group tab.
    const extraGroupIds = [...cloneApplyGroupIds].filter(
      (gid) => !(assignMode === 'group' && selectedGroupId === gid),
    );
    await applyGroupMemberships(userId, extraGroupIds);

    // Same guard for roles already covered by the primary Individual Roles tab.
    const extraRoles = cloneSourceAssignments.individualRoles.filter((r) => {
      if (!cloneApplyRoleIds.has(r.roleId)) return false;
      if (assignMode === 'roles' && selectedRoleIds.includes(r.roleId)) return false;
      return true;
    });
    await applyIndividualRoleAssignments(
      userId,
      extraRoles.map((r) => ({ roleId: r.roleId, scopeKind: r.scopeKind, scopeId: r.scopeId })),
    );
  };

  const writeNodeAssignment = async (userId: string) => {
    if (!selectedOrgChartNodeId) return;
    const supabase = getSupabaseClient();
    const { error: naErr } = await supabase.from('user_node_assignments').upsert({
      user_id: userId,
      org_unit_id: selectedOrgChartNodeId,
      active: true,
      granted_by: createdBy || 'admin',
      grant_source: 'manual',
    }, { onConflict: 'user_id,org_unit_id' });
    if (naErr) console.warn('[CreateUserModal] node assignment write failed:', naErr.message);
  };

  const handleLinkExisting = async () => {
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) {
      setError('Email is required.');
      return;
    }
    if (!tenantId) {
      setError('No tenant context found. Please make sure a workspace is selected.');
      return;
    }
    if (!workspaceId) {
      setError('An active workspace is required to add someone. Select a workspace in the tenant switcher.');
      return;
    }
    setError(null);
    setLinkingExisting(true);
    try {
      const result = await linkExistingUserToWorkspace({
        email: trimmedEmail,
        name: name.trim() || undefined,
        tenantId,
        workspaceId,
        role: 'member',
        createdBy: createdBy || undefined,
      });
      await writeNodeAssignment(result.userId);
      await applyClonedExtras(result.userId);
      setCreatedEmail(trimmedEmail);
      await onSuccess(result.userId, buildAssignment(), { linkedExisting: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add existing user');
    } finally {
      setLinkingExisting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || !password) {
      setError('Email and password are required.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!tenantId) {
      setError('No tenant context found. Please make sure a workspace is selected.');
      return;
    }
    setCreating(true);
    try {
      const result = await createUserWithPassword({
        email: trimmedEmail,
        password,
        name: name.trim() || undefined,
        tenantId,
        workspaceId: workspaceId || undefined,
        role: 'member',
        createdBy: createdBy || undefined,
      });
      await writeNodeAssignment(result.userId);
      await applyClonedExtras(result.userId);
      setCreatedEmail(trimmedEmail);
      await onSuccess(result.userId, buildAssignment(), { linkedExisting: Boolean(result.linked) });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-raised)] shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[var(--rule)]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[var(--coral-dim)]">
              <UserPlus className="h-5 w-5 text-[var(--coral)]" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-[var(--text-pri)]">Create User</h2>
              <p className="text-xs text-[var(--text-ter)] mt-0.5">Account is ready immediately — no email confirmation required</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Success state */}
        {createdEmail ? (
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="p-3 bg-[var(--green-dim)] rounded-full w-fit mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-[var(--green)]" />
              </div>
              <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-1">User created!</h3>
              <p className="text-[var(--text-ter)] text-sm">
                <strong>{createdEmail}</strong> can log in at <strong>/auth/login</strong> using the password you set.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setCreatedEmail(null);
                  setName(''); setEmail(''); setPassword('');
                  setSelectedPkg(null); setSelectedGroupId('');
                  setSelectedRoleIds([]); setSelectedOrgUnitId('');
                  setSelectedOrgChartNodeId('');
                  setCloneEnabled(false); setCloneSourceUserId('');
                  setCloneSourceAssignments(null);
                  setCloneApplyGroupIds(new Set()); setCloneApplyRoleIds(new Set());
                }}
                className="flex-1 px-4 py-2 border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
              >
                Create Another
              </button>
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-[var(--bg)] text-white hover:bg-[var(--bg-raised)] transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Smith"
                className="w-full px-4 py-3 border border-[var(--rule)] focus:ring-2 focus:ring-red-500 focus:border-[var(--coral)]"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
                Email Address <span className="text-[var(--coral)]">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                required
                className="w-full px-4 py-3 border border-[var(--rule)] focus:ring-2 focus:ring-red-500 focus:border-[var(--coral)]"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
                Password <span className="text-[var(--coral)]">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                  className="flex-1 px-4 py-3 border border-[var(--rule)] focus:ring-2 focus:ring-red-500 focus:border-[var(--coral)]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="px-3 py-3 border border-[var(--rule)] text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-[var(--text-ter)]">
                Share this with the user so they can log in immediately. If they already have an account with this email,
                use <strong className="text-[var(--text-sec)]">Add existing user to workspace</strong> below instead of creating a
                duplicate login.
              </p>
            </div>

            {/* Clone from existing user */}
            {cloneCandidates.length > 0 && (
              <div className="border border-[var(--rule)] bg-[var(--surface)]/60 p-4">
                <label className="flex items-center justify-between gap-3 cursor-pointer select-none">
                  <span className="flex items-center gap-2 text-sm font-medium text-[var(--text-sec)]">
                    <Copy className="h-4 w-4 text-[var(--text-ter)]" />
                    Start from an existing user's configuration
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={cloneEnabled}
                    onClick={() => {
                      const next = !cloneEnabled;
                      setCloneEnabled(next);
                      if (!next) setCloneSourceUserId('');
                    }}
                    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                      cloneEnabled ? 'bg-[var(--slate)]' : 'bg-[var(--rule-bright)]'
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-[var(--bg-raised)] transition-transform ${
                        cloneEnabled ? 'translate-x-4' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </label>

                {cloneEnabled && (
                  <div className="mt-3 space-y-3">
                    <SearchableSelect
                      options={cloneCandidates.map((c) => ({ value: c.id, label: `${c.name} (${c.email})` }))}
                      value={cloneSourceUserId}
                      onChange={setCloneSourceUserId}
                      placeholder="Search for a user to clone…"
                    />

                    {cloneLoading && (
                      <p className="flex items-center gap-1.5 text-xs text-[var(--text-ter)]">
                        <RefreshCw className="h-3 w-3 animate-spin" />
                        Loading their current configuration…
                      </p>
                    )}

                    {cloneError && (
                      <p className="text-xs text-[var(--coral)]">{cloneError}</p>
                    )}

                    {cloneSourceAssignments && !cloneLoading && (
                      <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-3 space-y-2">
                        <p className="text-xs font-medium text-[var(--text-ter)]">
                          Will apply to the new user — click a badge to remove or re-add it:
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {cloneSourceAssignments.packageSlug && (() => {
                            const pkg = getRolePackage(cloneSourceAssignments.packageSlug!);
                            if (!pkg) return null;
                            const active = assignMode === 'package' && selectedPkg?.slug === pkg.slug;
                            return (
                              <CloneBadge
                                key="clone-pkg"
                                kind="Package"
                                label={pkg.displayName}
                                active={active}
                                onToggle={() => {
                                  if (active) {
                                    setSelectedPkg(null);
                                  } else {
                                    setAssignMode('package');
                                    setSelectedPkg(pkg);
                                  }
                                }}
                              />
                            );
                          })()}

                          {cloneSourceAssignments.groupIds.map((gid) => {
                            const group = groups.find((g) => g.id === gid);
                            const active = cloneApplyGroupIds.has(gid);
                            return (
                              <CloneBadge
                                key={`clone-group-${gid}`}
                                kind="Group"
                                label={group?.name || 'Unknown group'}
                                active={active}
                                onToggle={() =>
                                  setCloneApplyGroupIds((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(gid)) next.delete(gid);
                                    else next.add(gid);
                                    return next;
                                  })
                                }
                              />
                            );
                          })}

                          {cloneSourceAssignments.individualRoles.map((role) => {
                            const active = cloneApplyRoleIds.has(role.roleId);
                            return (
                              <CloneBadge
                                key={`clone-role-${role.roleId}`}
                                kind="Role"
                                label={role.roleLabel}
                                active={active}
                                onToggle={() =>
                                  setCloneApplyRoleIds((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(role.roleId)) next.delete(role.roleId);
                                    else next.add(role.roleId);
                                    return next;
                                  })
                                }
                              />
                            );
                          })}

                          {cloneSourceAssignments.orgUnitId && (() => {
                            const orgUnitId = cloneSourceAssignments.orgUnitId!;
                            const ou = orgUnits.find((o) => o.id === orgUnitId);
                            const active = selectedOrgChartNodeId === orgUnitId;
                            return (
                              <CloneBadge
                                key="clone-orgunit"
                                kind="Org unit"
                                label={ou?.displayName || 'Org unit'}
                                active={active}
                                onToggle={() => setSelectedOrgChartNodeId(active ? '' : orgUnitId)}
                              />
                            );
                          })()}

                          {!cloneSourceAssignments.packageSlug &&
                            cloneSourceAssignments.groupIds.length === 0 &&
                            cloneSourceAssignments.individualRoles.length === 0 &&
                            !cloneSourceAssignments.orgUnitId && (
                              <p className="text-xs text-[var(--text-ter)] italic">
                                This user has no active roles, groups, or org placement to clone.
                              </p>
                            )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Role / Package / Group assignment */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
                Assign roles <span className="text-[var(--text-ter)] font-normal">(optional)</span>
              </label>

              {/* Tab selector */}
              <div className="flex border border-[var(--rule)] overflow-hidden mb-3">
                {(
                  [
                    { id: 'package' as CreateAssignMode, label: 'Package', color: 'indigo' },
                    { id: 'group' as CreateAssignMode, label: 'Group', color: 'purple' },
                    { id: 'roles' as CreateAssignMode, label: 'Individual Roles', color: 'blue' },
                  ] as const
                ).map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setAssignMode(tab.id)}
                    className={`flex-1 py-1.5 text-xs font-medium transition-colors ${
                      assignMode === tab.id
                        ? tab.color === 'indigo'
                          ? 'bg-[var(--slate)] text-white'
                          : tab.color === 'purple'
                            ? 'bg-[var(--slate)] text-white'
                            : 'bg-[var(--slate)] text-white'
                        : 'bg-[var(--bg-raised)] text-[var(--text-sec)] hover:bg-[var(--surface)]'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Package tab */}
              {assignMode === 'package' && (
                <div className="space-y-1.5">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
                    <input
                      value={pkgQuery}
                      onChange={(e) => setPkgQuery(e.target.value)}
                      placeholder="Search packages…"
                      className="w-full pl-8 pr-3 py-1.5 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1 pr-0.5">
                    {ROLE_PACKAGES
                      .filter((p) => {
                        const lq = pkgQuery.toLowerCase();
                        return !lq || p.displayName.toLowerCase().includes(lq) || p.typicalPersona.toLowerCase().includes(lq);
                      })
                      .sort((a, b) => {
                        // Persona packages first
                        if (a.theme === 'cross-functional' && b.theme !== 'cross-functional') return -1;
                        if (a.theme !== 'cross-functional' && b.theme === 'cross-functional') return 1;
                        return 0;
                      })
                      .map((pkg) => {
                        const isSelected = selectedPkg?.slug === pkg.slug;
                        const primaryCount = pkg.primaryRoleSlug?.length ?? 0;
                        return (
                          <button
                            key={pkg.slug}
                            type="button"
                            onClick={() => setSelectedPkg(isSelected ? null : pkg)}
                            className={`w-full text-left  border px-3 py-2 text-sm transition-colors ${
                              isSelected
                                ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)]'
                                : 'border-[var(--rule)] hover:border-[var(--slate)]'
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium truncate">{pkg.displayName}</span>
                              <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                pkg.theme === 'cross-functional'
                                  ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
                                  : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'
                              }`}>
                                {pkg.theme === 'cross-functional' ? 'Persona' : ROLE_PACKAGE_THEME_LABELS[pkg.theme]}
                              </span>
                            </div>
                            <p className="text-xs text-[var(--text-ter)] truncate mt-0.5">{pkg.typicalPersona}</p>
                            {primaryCount > 0 && (
                              <p className="text-[10px] text-[var(--text-ter)] mt-0.5">
                                {primaryCount} primary · {pkg.roleSlug.length - primaryCount} supporting
                              </p>
                            )}
                          </button>
                        );
                      })}
                  </div>
                  {selectedPkg && (
                    <p className="text-xs text-[var(--text-sec)] bg-[var(--slate-dim)] border border-[var(--slate)] rounded px-2 py-1">
                      <strong>{selectedPkg.displayName}</strong> will be applied ({selectedPkg.roleSlug.length} roles)
                    </p>
                  )}
                </div>
              )}

              {/* Group tab */}
              {assignMode === 'group' && (
                <div className="space-y-1.5">
                  {groups.length === 0 ? (
                    <p className="text-sm text-[var(--text-ter)] italic">No groups exist yet. Create groups first.</p>
                  ) : (
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {groups.map((g) => (
                        <button
                          key={g.id}
                          type="button"
                          onClick={() => setSelectedGroupId(selectedGroupId === g.id ? '' : g.id)}
                          className={`w-full text-left  border px-3 py-2 text-sm transition-colors ${
                            selectedGroupId === g.id
                              ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)]'
                              : 'border-[var(--rule)] hover:border-[var(--slate)]'
                          }`}
                        >
                          <span className="font-medium">{g.name}</span>
                          <span className="ml-2 text-xs text-[var(--text-ter)] capitalize">{g.type}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedGroupId && (
                    <p className="text-xs text-[var(--text-sec)] bg-[var(--slate-dim)] border border-[var(--slate)] rounded px-2 py-1">
                      User will inherit <strong>{groups.find(g => g.id === selectedGroupId)?.name}</strong>'s roles
                    </p>
                  )}
                </div>
              )}

              {/* Individual roles tab */}
              {assignMode === 'roles' && (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
                    <input
                      value={roleQuery}
                      onChange={(e) => setRoleQuery(e.target.value)}
                      placeholder="Search roles…"
                      className="w-full pl-8 pr-3 py-1.5 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="max-h-44 overflow-y-auto space-y-2 pr-0.5">
                    {Object.entries(JOB_FAMILIES).map(([familyId, family]) => {
                      const familyRoles = DEFAULT_JOB_ROLES.filter(
                        (r) => r.family === familyId &&
                          (!roleQuery || r.displayName.toLowerCase().includes(roleQuery.toLowerCase()))
                      );
                      if (familyRoles.length === 0) return null;
                      return (
                        <div key={familyId}>
                          <p className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-1">{family.label}</p>
                          <div className="space-y-0.5">
                            {familyRoles.map((jobRole) => {
                              const dbRole = roles.find((r) => r.label === jobRole.displayName || r.label.toLowerCase().includes(jobRole.slug));
                              if (!dbRole) return null;
                              const isSelected = selectedRoleIds.includes(dbRole.id);
                              return (
                                <button
                                  key={dbRole.id}
                                  type="button"
                                  onClick={() => toggleRole(dbRole.id)}
                                  className={`w-full text-left  border px-3 py-1.5 text-sm transition-colors ${
                                    isSelected
                                      ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)]'
                                      : 'border-[var(--rule)] hover:border-[var(--slate)]'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    {isSelected ? <Check className="h-3.5 w-3.5 shrink-0" /> : <Shield className="h-3.5 w-3.5 text-[var(--text-ter)] shrink-0" />}
                                    <span className="font-medium truncate">{dbRole.label}</span>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {selectedRoleIds.length > 0 && (
                    <div>
                      <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Scope</label>
                      <select
                        value={selectedOrgUnitId}
                        onChange={(e) => setSelectedOrgUnitId(e.target.value)}
                        className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-1.5 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Workspace-wide</option>
                        {orgUnits.map(ou => (
                          <option key={ou.id} value={ou.id}>
                            {'—'.repeat((ou as OrgUnit & { depth?: number }).depth ?? 0)} {ou.displayName ?? ou.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Org chart placement */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
                <span className="flex items-center gap-1.5">
                  <Building2 className="h-4 w-4 text-[var(--text-ter)]" />
                  Org chart placement
                  <span className="text-[var(--text-ter)] font-normal">(optional)</span>
                </span>
              </label>
              <select
                value={selectedOrgChartNodeId}
                onChange={(e) => setSelectedOrgChartNodeId(e.target.value)}
                className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2.5 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-gray-400 focus:border-transparent"
              >
                <option value="">No placement — unassigned</option>
                {orgUnits.map(ou => (
                  <option key={ou.id} value={ou.id}>
                    {'—'.repeat((ou as OrgUnit & { depth?: number }).depth ?? 0)} {ou.displayName ?? ou.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-[var(--text-ter)]">
                Places this user in a node on the org chart. Affects catchball routing and scoped visibility.
              </p>
            </div>

            {/* Error + recover when email already exists in Auth */}
            {error && (
              <div className="space-y-3">
                <div className="p-4 bg-[var(--coral-dim)] border border-[var(--coral)] flex items-start gap-3 text-[var(--coral)] text-sm">
                  <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p>{error}</p>
                    {isAuthEmailAlreadyRegisteredMessage(error) && workspaceId ? (
                      <p className="mt-2 text-[var(--coral)]/90 text-xs">
                        Their login already exists (often from another workspace). You can grant access here without a
                        new password.
                      </p>
                    ) : null}
                  </div>
                </div>
                {isAuthEmailAlreadyRegisteredMessage(error) && workspaceId ? (
                  <button
                    type="button"
                    disabled={linkingExisting || !email.trim()}
                    onClick={() => void handleLinkExisting()}
                    className="w-full px-4 py-3 border border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)] text-sm font-medium hover:bg-[var(--slate-dim)] disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {linkingExisting ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Adding to workspace…
                      </>
                    ) : (
                      <>
                        <UserCheck className="h-4 w-4" />
                        Add existing user to this workspace
                      </>
                    )}
                  </button>
                ) : null}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating || !email.trim() || !password}
                className="flex-1 px-4 py-3 bg-[var(--coral)] text-white hover:bg-[var(--coral)] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {creating ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Creating…
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4" />
                    Create User
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Reset Password Modal
// ============================================================================

interface ResetPasswordModalProps {
  user: UserWithPermissions;
  onReset: (newPassword: string) => Promise<void>;
  onClose: () => void;
}

function ResetPasswordModal({ user, onReset, onClose }: ResetPasswordModalProps) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passwordsMatch = newPassword === confirmPassword;
  const isValid = newPassword.length >= 8 && passwordsMatch;

  const handleSubmit = async () => {
    if (!isValid) return;
    setSaving(true);
    setError(null);
    try {
      await onReset(newPassword);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-[var(--text-sec)]" />
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">Reset Password</h2>
          </div>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="text-sm text-[var(--text-sec)] mb-4">
          Set a new password for <span className="font-medium text-[var(--text-pri)]">{user.name}</span>{' '}
          <span className="text-[var(--text-ter)]">({user.email})</span>
        </p>

        {error && (
          <div className="flex items-start gap-2 text-sm text-[var(--coral)] bg-[var(--coral-dim)] border border-[var(--coral)] p-3 mb-4">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              New Password <span className="text-[var(--coral)]">*</span>
            </label>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full border border-[var(--rule)] px-3 py-2 pr-10 focus:ring-2 focus:ring-blue-500 focus:border-[var(--slate)]"
                placeholder="Minimum 8 characters"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                tabIndex={-1}
              >
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {newPassword.length > 0 && newPassword.length < 8 && (
              <p className="text-xs text-[var(--coral)] mt-1">Must be at least 8 characters</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Confirm Password <span className="text-[var(--coral)]">*</span>
            </label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full border border-[var(--rule)] px-3 py-2 pr-10 focus:ring-2 focus:ring-blue-500 focus:border-[var(--slate)]"
                placeholder="Re-enter new password"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                tabIndex={-1}
              >
                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {confirmPassword.length > 0 && !passwordsMatch && (
              <p className="text-xs text-[var(--coral)] mt-1">Passwords do not match</p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)]"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValid || saving}
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] flex items-center gap-2"
          >
            {saving ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Resetting...
              </>
            ) : (
              <>
                <KeyRound className="h-4 w-4" />
                Reset Password
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Edit User Modal
// ============================================================================

/**
 * People added without a login carry a `manual_` external id. Everyone else's
 * email is their sign-in, which update_person_details will not change, and only
 * a platform admin or the person themselves may rename them there.
 */
function hasSignIn(user: UserWithPermissions): boolean {
  return !user.externalId?.startsWith('manual_');
}

interface EditUserModalProps {
  user: UserWithPermissions;
  onSave: (userId: string, updates: { name: string; email: string }) => void;
  onClose: () => void;
}

function EditUserModal({ user, onSave, onClose }: EditUserModalProps) {
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim() || !email.trim()) return;
    setSaving(true);
    try {
      await onSave(user.id, { name: name.trim(), email: email.trim() });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-[var(--text-pri)]">Edit User</h2>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Full Name <span className="text-[var(--coral)]">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-[var(--slate)]"
              placeholder="John Doe"
            />
          </div>

          {hasSignIn(user) ? (
            <div>
              <p className="block text-sm font-medium text-[var(--text-sec)] mb-1">Email Address</p>
              <p className="text-sm text-[var(--text-pri)]">{user.email}</p>
              <p className="text-xs text-[var(--text-ter)] mt-1">This is their sign-in email, so it does not change here.</p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Email Address <span className="text-[var(--coral)]">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-[var(--slate)]"
                placeholder="john@example.com"
              />
            </div>
          )}

          <div className="text-xs text-[var(--text-ter)] bg-[var(--surface)] p-3">
            <p>User ID: {user.id}</p>
            <p>External ID: {user.externalId || 'None'}</p>
            <p>Created: {new Date(user.createdAt).toLocaleDateString()}</p>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)]"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!name.trim() || !email.trim() || saving}
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] flex items-center gap-2"
          >
            {saving ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Pencil className="h-4 w-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
